import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { Button, InfoLabel, Spinner } from '@fluentui/react-components';
import {
    CheckmarkCircle16Filled,
    Warning16Filled,
    OpenRegular,
} from '@fluentui/react-icons';

import * as ComponentFunctions from './ContractReviewStatus.Functions';
import './ContractReviewStatus.Styles.css';

const UNREVIEWED_RISK =
    'Sus conceptos y valores pueden no ser definitivos. Liquidar así puede derivar en ' +
    'importes incorrectos o en ítems que queden sin liquidar.';

const ContractReviewStatusComponent = (props: ComponentFunctions.Props) => {

    const { ContractId, Contract } = props;

    // The consumption screen loads the protocol, not its contract, so without
    // this the review state would silently never resolve.
    useEffect(() => {
        if (ContractId && Contract === undefined) {
            props.GetContractById(ContractId);
        }
    }, [ContractId, Contract === undefined]);

    // Read before the early returns below: hooks cannot live after them.
    const isReviewed = Contract?.contractVerified === true;

    const [isCelebrating, setIsCelebrating] = useState<boolean>(false);
    const wasReviewedRef = useRef<boolean>(false);
    const iconRef = useRef<HTMLSpanElement | null>(null);
    const [okAnchor, setOkAnchor] = useState<{ top: number; left: number } | null>(null);

    // Fires on the transition into "reviewed", not on every render, so it plays
    // once when the good news arrives instead of on each re-render.
    useEffect(() => {
        if (isReviewed && !wasReviewedRef.current) {
            setIsCelebrating(true);
            const timer = setTimeout(() => setIsCelebrating(false), 3500);
            wasReviewedRef.current = true;
            return () => clearTimeout(timer);
        }
        wasReviewedRef.current = isReviewed;
    }, [isReviewed]);

    // The toolbar and the page header both sit at z-index 20, so a thumb drawn
    // inside the toolbar can never rise above the header: children cannot escape
    // their parent's stacking context. It is measured here and drawn on the body
    // instead, which is the only way out.
    useEffect(() => {
        if (!isCelebrating) {
            setOkAnchor(null);
            return;
        }
        const icon = iconRef.current;
        if (!icon) {
            return;
        }
        const rect = icon.getBoundingClientRect();
        setOkAnchor({ top: rect.top + rect.height / 2, left: rect.left + rect.width / 2 });
    }, [isCelebrating]);

    // No association means there is nothing to state: staying silent beats
    // implying the contract is unreviewed when there is no contract at all.
    if (!ContractId) {
        return null;
    }

    if (Contract === undefined) {
        return (
            <div className='contract-review contract-review--loading'>
                <Spinner size='extra-tiny' />
                <span className='contract-review__label'>Verificando contrato...</span>
            </div>
        );
    }

    const openContract = () => {
        window.open('/contracts/' + ContractId, '_blank', 'noopener,noreferrer');
    };

    if (isReviewed) {
        return (
            <div className='contract-review contract-review--reviewed'>
                <span className='contract-review__icon-wrap' ref={iconRef}>
                    <CheckmarkCircle16Filled
                        className={isCelebrating
                            ? 'contract-review__icon contract-review__icon--pop'
                            : 'contract-review__icon'}
                    />
                </span>
                {/* Decorative flourish: hidden from assistive tech, since the
                    label already says the contract is reviewed. */}
                {okAnchor && createPortal(
                    <span
                        className='contract-review__ok'
                        aria-hidden='true'
                        style={{ top: okAnchor.top, left: okAnchor.left }}
                    >
                        👍
                    </span>,
                    document.body
                )}
                <span className='contract-review__label'>El contrato está revisado</span>
            </div>
        );
    }

    return (
        <div className='contract-review contract-review--pending'>
            <Warning16Filled className='contract-review__icon' />
            <span className='contract-review__label'>
                El contrato no está revisado
                {/* inline defaults to true, so the callout rendered inside the
                    toolbar and was clipped by it. Portalled, it escapes the bar. */}
                <InfoLabel
                    className='contract-review__info'
                    /* Capped width so the surface wraps instead of stretching
                       the sentence across one very long line. */
                    info={{ children: UNREVIEWED_RISK, className: 'contract-review__callout' }}
                    infoButton={{ inline: false }}
                />
            </span>
            <Button
                appearance='subtle'
                size='small'
                className='contract-review__action'
                icon={<OpenRegular />}
                iconPosition='after'
                onClick={openContract}
            >
                Ir al contrato
            </Button>
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ContractReviewStatusComponent as any);
